/**
 * External system form — CoordioCore form-page-pattern.
 *
 * Layout: PageFormLayout (ShellHeader + SectionSideNav grid + panel sections)
 * Fields: @coordio/ui FormField (label + ⓘ tooltip + error + required asterisk)
 * Cards:  @coordio/ui FormSection (title + description + bordered body)
 * Toggle: @coordio/ui FormChoiceField (label + Switch in horizontal row)
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe, Lock, Settings } from 'lucide-react';

import { CoordioDropdown, FormField, Input, Switch, Textarea } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { PageFormLayout } from '../layouts/PageFormLayout';
import { externalSystemService } from '../../services/externalSystemService';
import { useNotificationStore } from '../../store/notificationStore';

const AUTHENTICATION_TYPES = [
  { value: 'None', label: 'None' },
  { value: 'ApiKey', label: 'API Key' },
  { value: 'BearerToken', label: 'Bearer Token' },
  { value: 'BasicAuth', label: 'Basic Auth' },
  { value: 'OAuth2', label: 'OAuth 2.0' },
  { value: 'JWT', label: 'JWT' },
  { value: 'CustomHeader', label: 'Custom Header' },
  { value: 'RequestSigning', label: 'Request Signing' },
];

/**
 * External system form component for creating and editing external systems.
 */
export const ExternalSystemForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [authenticationType, setAuthenticationType] = useState('None');
  const [authenticationConfigJson, setAuthenticationConfigJson] = useState('');
  const [defaultHeadersJson, setDefaultHeadersJson] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isEdit && id) {
      loadSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadSystem = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await externalSystemService.getExternalSystem(id);
      setName(data.name);
      setDescription(data.description || '');
      setBaseUrl(data.baseUrl);
      setAuthenticationType(data.authenticationType);
      setAuthenticationConfigJson('');
      setDefaultHeadersJson(data.defaultHeadersJson || '');
      setIsActive(data.isActive);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load external system';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!baseUrl.trim()) {
      setError('Base URL is required');
      return;
    }

    try {
      new URL(baseUrl.trim());
    } catch {
      setError('Base URL must be a valid URL');
      return;
    }

    if (authenticationConfigJson.trim()) {
      try {
        JSON.parse(authenticationConfigJson.trim());
      } catch {
        setError('Authentication configuration JSON must be valid JSON');
        return;
      }
    }

    if (defaultHeadersJson.trim()) {
      try {
        JSON.parse(defaultHeadersJson.trim());
      } catch {
        setError('Default headers JSON must be valid JSON');
        return;
      }
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        baseUrl: baseUrl.trim(),
        authenticationType,
        authenticationConfigJson: authenticationConfigJson.trim() || undefined,
        defaultHeadersJson: defaultHeadersJson.trim() || undefined,
        isActive,
      };

      if (isEdit && id) {
        await externalSystemService.updateExternalSystem(id, data);
        addNotification('External system updated successfully', 'success');
      } else {
        await externalSystemService.createExternalSystem(data);
        addNotification('External system created successfully', 'success');
      }
      navigate('/external-systems');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save external system';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const getAuthConfigHelperText = () => {
    switch (authenticationType) {
      case 'OAuth2':
        return 'JSON: { "clientId": "...", "clientSecret": "...", "tokenUrl": "...", "scope": "..." }';
      case 'JWT':
        return 'JSON: { "secret": "...", "issuer": "...", "audience": "..." }';
      case 'ApiKey':
        return 'JSON: { "key": "...", "location": "header|query", "headerName": "...", "queryParameterName": "..." }';
      case 'BasicAuth':
        return 'JSON: { "username": "...", "password": "..." }';
      case 'BearerToken':
        return 'JSON: { "token": "..." }';
      case 'CustomHeader':
        return 'JSON: { "headerName": "...", "headerValue": "..." }';
      case 'RequestSigning':
        return 'JSON: { "secret": "...", "signatureHeaderName": "..." }';
      default:
        return 'Authentication configuration JSON (optional)';
    }
  };

  return (
    <PageFormLayout
      title={isEdit ? 'Edit External System' : 'Create External System'}
      subtitle="Configure an external API endpoint with authentication and default headers."
      data-testid="external-system-form-page"
      loading={loading}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to List', href: '/external-systems' }}
      actions={{
        onSave: handleSubmit,
        onCancel: () => navigate('/external-systems'),
        saveLabel: isEdit ? 'Update' : 'Create',
        saving,
      }}
      navGroups={[
        {
          title: 'Profile',
          items: [
            { id: 'details', label: 'Details', icon: <Globe size={16} /> },
          ],
        },
        {
          title: 'Configuration',
          items: [
            { id: 'auth', label: 'Authentication', icon: <Lock size={16} /> },
            { id: 'advanced', label: 'Advanced', icon: <Settings size={16} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'details',
          title: 'Details',
          description: 'Name, description, and base URL for this external system.',
          icon: <Globe size={20} />,
          content: (
            <FormSection
              title="System identity"
              description="The name and base URL identify this integration across the platform."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Name"
                  required
                  description="Unique name for this external system."
                  data-testid="external-system-name"
                >
                  <Input
                    className="w-full"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="external-system-name"
                  />
                </FormField>

                <FormField
                  label="Base URL"
                  required
                  description="The base URL for the external API (e.g., https://api.example.com)."
                  data-testid="external-system-baseUrl"
                >
                  <Input
                    className="w-full"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    data-testid="external-system-baseUrl"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Description"
                  description="Optional context about what this external system connects to."
                  data-testid="external-system-description"
                >
                  <Textarea
                    className="w-full"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    data-testid="external-system-description"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },
        {
          id: 'auth',
          title: 'Authentication',
          description: 'Choose an authentication method and provide configuration details.',
          icon: <Lock size={20} />,
          content: (
            <FormSection
              title="Authentication settings"
              description="Select how requests to this external system are authenticated."
            >
              <FormField
                label="Authentication Type"
                description="The authentication method used when calling this external system."
                data-testid="external-system-authenticationType"
              >
                <CoordioDropdown
                  options={AUTHENTICATION_TYPES}
                  value={authenticationType}
                  onChange={(val) => setAuthenticationType(val ?? 'None')}
                  clearable={false}
                  data-testid="external-system-authenticationType"
                />
              </FormField>

              {authenticationType !== 'None' && (
                <div className="mt-5">
                  <FormField
                    label="Authentication Configuration"
                    description={getAuthConfigHelperText()}
                    data-testid="external-system-authConfig"
                  >
                    <Textarea
                      className="w-full"
                      value={authenticationConfigJson}
                      onChange={(e) => setAuthenticationConfigJson(e.target.value)}
                      rows={6}
                      data-testid="external-system-authConfig"
                    />
                  </FormField>
                </div>
              )}
            </FormSection>
          ),
        },
        {
          id: 'advanced',
          title: 'Advanced',
          description: 'Default headers and active status.',
          icon: <Settings size={20} />,
          content: (
            <FormSection
              title="Advanced configuration"
              description="Set default request headers and control whether this system is active."
            >
              <FormField
                label="Default Headers JSON"
                description='JSON object with default headers (e.g., { "Accept": "application/json" }).'
                data-testid="external-system-defaultHeaders"
              >
                <Textarea
                  className="w-full"
                  value={defaultHeadersJson}
                  onChange={(e) => setDefaultHeadersJson(e.target.value)}
                  rows={4}
                  data-testid="external-system-defaultHeaders"
                />
              </FormField>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={isActive}
                        onCheckedChange={setIsActive}
                        data-testid="external-system-isActive"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
